import { useEffect, useState } from "react";
import api, { fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, ArrowRight } from "lucide-react";

const STATUS = {
  requested: "bg-amber-100 text-amber-800 border-amber-200",
  sent: "bg-blue-100 text-blue-800 border-blue-200",
  received: "bg-green-100 text-green-800 border-green-200",
};

export default function Transfers() {
  const { isPriv, user } = useAuth();
  const [rows, setRows] = useState([]);
  const [branches, setBranches] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ item_type: "product", item_id: "", qty: 1, from_branch: "", to_branch: "" });

  const load = () => { setLoading(true); api.get("/transfers").then(({ data }) => setRows(data)).finally(() => setLoading(false)); };
  useEffect(() => {
    load();
    api.get("/branches").then(({ data }) => setBranches(data)).catch(() => {});
  }, []);
  useEffect(() => {
    const ep = form.item_type === "product" ? "/products" : "/spareparts";
    api.get(ep).then(({ data }) => setItems(data)).catch(() => {});
  }, [form.item_type]);

  const create = async () => {
    const it = items.find((x) => x.id === form.item_id);
    if (!it || !form.from_branch || !form.to_branch) return toast.error("Lengkapi item & cabang");
    if (form.from_branch === form.to_branch) return toast.error("Cabang asal & tujuan harus berbeda");
    try {
      await api.post("/transfers", {
        item_type: form.item_type, item_id: form.item_id, item_name: it.name,
        qty: Number(form.qty || 1), from_branch: form.from_branch, to_branch: form.to_branch,
      });
      toast.success("Permintaan transfer dibuat"); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const act = async (r, action) => {
    try { await api.put(`/transfers/${r.id}/${action}`); toast.success("Berhasil"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  return (
    <div>
      <PageHeader title="Transfer / Mutasi Barang" subtitle="Pindahkan stok antar cabang dengan pencatatan jelas">
        <Button onClick={() => { setForm({ item_type: "product", item_id: "", qty: 1, from_branch: "", to_branch: user?.branch_id || "" }); setOpen(true); }} className="bg-sky-600 hover:bg-sky-700" data-testid="transfer-add-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Minta Transfer
        </Button>
      </PageHeader>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200"><tr>
            {["Tanggal", "Item", "Qty", "Dari", "Ke", "Status", "Diminta", "Aksi"].map((h) => <th key={h} className="px-4 py-3 text-left font-medium text-slate-500">{h}</th>)}
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? <tr><td colSpan={8}><Loading /></td></tr>
              : rows.length === 0 ? <tr><td colSpan={8}><Empty /></td></tr>
              : rows.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-slate-500">{fmtDate(r.created_at)}</td>
                  <td className="px-4 py-3">{r.item_name} <span className="text-xs text-slate-400">({r.item_type === "product" ? "Produk" : "Sparepart"})</span></td>
                  <td className="px-4 py-3 font-mono-num">{r.qty}</td>
                  <td className="px-4 py-3">{r.from_name}</td>
                  <td className="px-4 py-3">{r.to_name}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium border capitalize ${STATUS[r.status] || ""}`}>{r.status}</span></td>
                  <td className="px-4 py-3 text-slate-500">{r.requested_by}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {isPriv && r.status === "requested" && <Button size="sm" variant="outline" className="text-xs" onClick={() => act(r, "send")} data-testid={`transfer-send-${r.id}`}>Kirim</Button>}
                    {isPriv && r.status === "sent" && <Button size="sm" variant="outline" className="text-xs" onClick={() => act(r, "receive")} data-testid={`transfer-receive-${r.id}`}>Terima</Button>}
                    {r.status === "received" && <span className="text-xs text-green-600">Selesai</span>}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Minta Transfer Barang</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs text-slate-500">Jenis Item</Label>
              <Select value={form.item_type} onValueChange={(v) => setForm({ ...form, item_type: v, item_id: "" })}>
                <SelectTrigger className="mt-1" data-testid="transfer-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="product">Produk / HP</SelectItem><SelectItem value="sparepart">Sparepart</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Item</Label>
              <Select value={form.item_id} onValueChange={(v) => setForm({ ...form, item_id: v })}>
                <SelectTrigger className="mt-1" data-testid="transfer-item"><SelectValue placeholder="Pilih item" /></SelectTrigger>
                <SelectContent>{items.map((x) => <SelectItem key={x.id} value={x.id}>{x.name} (stok {x.stock})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs text-slate-500">Jumlah</Label><Input type="number" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} className="mt-1" data-testid="transfer-qty" /></div>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label className="text-xs text-slate-500">Dari Cabang</Label>
                <Select value={form.from_branch} onValueChange={(v) => setForm({ ...form, from_branch: v })}>
                  <SelectTrigger className="mt-1" data-testid="transfer-from"><SelectValue placeholder="Asal" /></SelectTrigger>
                  <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <ArrowRight className="w-4 h-4 mb-2.5 text-slate-400" />
              <div className="flex-1">
                <Label className="text-xs text-slate-500">Ke Cabang</Label>
                <Select value={form.to_branch} onValueChange={(v) => setForm({ ...form, to_branch: v })}>
                  <SelectTrigger className="mt-1" data-testid="transfer-to"><SelectValue placeholder="Tujuan" /></SelectTrigger>
                  <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Batal</Button><Button onClick={create} className="bg-sky-600 hover:bg-sky-700" data-testid="transfer-save-btn">Kirim Permintaan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
